import domready from "domready"
import { polygonCentroid, polygonHull } from "d3-polygon"
import spectral from "spectral.js"
import "./style.css"
import { clamp, distance, findCircleCenter } from "./util"
import { allPalettesWithBlack } from "./randomPalette"
import weightedRandom from "./weightedRandom"

const PHI = (1 + Math.sqrt(5)) / 2;
const TAU = Math.PI * 2;
const DEG2RAD_FACTOR = TAU / 360;

const numLayers = 3 // gives Math.pow(2,numLayers) combinations

const config = {
    width: 0,
    height: 0
};

/**
 * @type CanvasRenderingContext2D
 */
let ctx;
let canvas;

function setSpectralJsColors(g, colorA, colorB, count)
{
    const pal = spectral.palette(colorA, colorB, count, spectral.HEX)
    const step = 1/(count-1)
    for (let i = 0; i < pal.length; i++)
    {
        g.addColorStop(i * step, pal[i])
    }
}

function getPair(palette, colorA = palette[0|Math.random() * palette.length])
{
    let colorB
    do
    {
        colorB = palette[0|Math.random() * palette.length]
    } while ( colorA === colorB)

    return [colorA, colorB]
}

function randomPoints(count = 100)
{
    const { width, height} = config

    let out = []

    for (let i = 0; i < count; i++)
    {
        out.push([
            Math.floor(width * Math.random()),
            Math.floor(height * Math.random()),
        ])
    }
    return out
}


/**
 * Rescales the given polygon around its centroid
 *
 * @param {number[number[]]} pts    Array of points
 * @param {number} t                scale
 * @return {number[number[]]}       scaled polygon
 */
function rescale(pts, t = 1)
{
    const centroid = polygonCentroid(pts)

    const [pt0,pt1,pt2] = pts

    const scalePoint = pt => {
        return [
            centroid[0] + (pt[0] - centroid[0]) * t,
            centroid[1] + (pt[1] - centroid[1]) * t,
        ]
    }

    return [
        scalePoint(pt0),
        scalePoint(pt1),
        scalePoint(pt2),
    ]
}

function createTemp(width,height)
{
    let canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height
    return canvas.getContext("2d")
}


function createTemps(count, width = config.width, height = config.height)
{

    const out = []

    for (let i = 0; i < count; i++)
    {
        out.push(
            createTemp(width, height)
        )
    }
    return out
}

const setupClip = weightedRandom([
    3, ctx => {
        const { width, height } = config

        const circleShape = (scale = 1) => {

            const [pt0,pt1,pt2] = rescale(randomPoints(3), scale)

            const center = findCircleCenter(
                pt0[0],
                pt0[1],
                pt1[0],
                pt1[1],
                pt2[0],
                pt2[1],
            )

            if (center)
            {
                const [cx,cy] = center
                const r = distance(cx,cy, pt0[0],pt0[1]);
                ctx.moveTo(cx + r, cy)
                ctx.arc(cx,cy,r,0,TAU, true)
            }
        }

        ctx.beginPath()

        if (Math.random() < 0.5)
        {
            ctx.moveTo(0, 0)
            ctx.lineTo(width, 0)
            ctx.lineTo(width, height)
            ctx.lineTo(0, height)
            ctx.lineTo(0, 0)
        }

        for (let i = 0; i < 3; i++)
        {
            circleShape(Math.random() < 0.5 ? 0.2 : 1)
        }

        ctx.clip("evenodd")
    },
    2, ctx => {
        const { width, height } = config

        const rectangleShape = (scale = 1) => {

            const x = Math.floor(Math.random() * width)
            const y = Math.floor(Math.random() * height)


            const angle = TAU/12 * Math.floor(Math.random() * 12)

            const w = Math.floor(100 + Math.random() * 400 * scale)
            const h = Math.floor(100 + Math.random() * 400 * scale)

            const vxx = Math.cos(angle) * w
            const vxy = Math.sin(angle) * w
            const vyx = Math.cos(angle + TAU/4) * h
            const vyy = Math.sin(angle + TAU/4) * h

            ctx.moveTo(x - vxx - vyx, y - vxy - vyy)
            ctx.lineTo(x + vxx - vyx, y + vxy - vyy)
            ctx.lineTo(x + vxx + vyx, y + vxy + vyy)
            ctx.lineTo(x - vxx + vyx, y - vxy + vyy)
            ctx.lineTo(x - vxx - vyx, y - vxy - vyy)
        }

        ctx.beginPath()

        if (Math.random() < 0.5)
        {
            ctx.moveTo(0, 0)
            ctx.lineTo(width, 0)
            ctx.lineTo(width, height)
            ctx.lineTo(0, height)
            ctx.lineTo(0, 0)
        }

        for (let i = 0; i < 3; i++)
        {
            rectangleShape(Math.random() < 0.5 ? 0.2 : 1)
        }

        ctx.clip("evenodd")
    }
])

function dither(ctx, blur = 50)
{
    const { width, height } = config

    const imageData = ctx.getImageData(0,0,width, height)

    const getOffset = (x,y) => {
        return ((clamp(y, height - 1)|0) * width + clamp(x,width-1)) << 2
    }

    const { data } = imageData

    let off = 0
    for (let y = 0; y < height; y++)
    {
        for (let x = 0; x < width; x++)
        {
            const angle = TAU * Math.random()
            const r = blur * Math.pow(Math.random(),3)
            const x0 = x + Math.cos(angle) * r
            const y0 = y + Math.sin(angle) * r

            const srcOff = getOffset(x0,y0)

            data[srcOff] = data[off]
            data[srcOff + 1] = data[off + 1]
            data[srcOff + 2] = data[off + 2]
            off += 4
        }
    }

    ctx.putImageData(imageData, 0 ,0)
}


function createGradients(palette)
{
    const { width, height } = config
    const out = []

    const cx = width >> 1
    const cy = height >> 1

    const radius = Math.sqrt(cx * cx + cy * cy)

    for (let i = 0; i < palette.length; i++)
    {
        const current = palette[i]

        const angle = TAU/12 * Math.floor(Math.random() * 12)
        const ca = Math.cos(angle)
        const sa = Math.sin(angle)
        const x0 = cx + ca * radius
        const y0 = cy + sa * radius
        const x1 = cx - ca * radius
        const y1 = cy - sa * radius

        const g = ctx.createLinearGradient(x0,y0,x1,y1)

        const [colorA, colorB] = getPair(palette, current)

        const colorC = spectral.mix(colorA, colorB, 0.9, spectral.HEX)
        setSpectralJsColors(g, colorA, colorC, Math.floor(radius))

        const c = createTemp(width, height)
        c.fillStyle = g
        c.fillRect(0,0,width,height)

        dither(c, 200)

        out.push(c.getImageData(0,0,width,height))
    }

    return out
}


function getTranslation(sources, bitMasks)
{
    const count = 1 << bitMasks.length
    const out = []
    for (let i = 0; i < count; i++)
    {
        out.push(
            0|Math.random() * sources.length
        )
    }

    console.log({sources, bitMasks, translation: out})

    return out
}


function combine(sources, bitMasks)
{
    const { width, height } = config


    const translation = getTranslation(sources, bitMasks)

    const ctx = createTemp(width, height)

    const dst = ctx.getImageData(0,0,width,height)

    let off = 0

    let min = Infinity, max = -Infinity
    for (let y = 0; y < height; y++)
    {
        for (let x = 0; x < width; x++)
        {
            let n = 0
            for (let i = 0; i < numLayers; i++)
            {
                n += bitMasks[i].data[off+1] > 128
            }

            n = translation[n]

            const srcData = sources[n].data
            dst.data[off    ] = srcData[off    ]
            dst.data[off + 1] = srcData[off + 1]
            dst.data[off + 2] = srcData[off + 2]
            dst.data[off + 3] = 255

            if (n < min)
            {
                min = n
            }
            if (n > max)
            {
                max = n
            }

            off += 4
        }
    }

    console.log({sources, bitMasks, min,max})

    ctx.putImageData(dst, 0, 0)

    return ctx
}


domready(
    () => {

        canvas = document.getElementById("screen");
        ctx = canvas.getContext("2d");

        const width = (window.innerWidth) | 0;
        const height = (window.innerHeight) | 0;

        config.width = width;
        config.height = height;

        canvas.width = width;
        canvas.height = height;



        const paint = () => {

            const palette = allPalettesWithBlack[0|Math.random() * allPalettesWithBlack.length]

            ctx.globalCompositeOperation = "source-over"
            ctx.fillStyle = "#000";
            ctx.fillRect(0,0, width, height);

            const temps = createTemps(numLayers)


            const paintMask = (ctx, color = "#fff") => {
                ctx.fillStyle = "#000";
                ctx.fillRect(0,0, width, height);
                ctx.save()
                setupClip(ctx)
                ctx.fillStyle = color
                ctx.fillRect(0,0, width, height);
                ctx.restore()

                //dither(ctx, 1 + Math.pow(Math.random(),3) * 200)
            }

            for (let i = 0; i < temps.length; i++)
            {
                paintMask(temps[i])
            }

            const gradients = createGradients(palette)

            const result = combine(gradients, temps.map(ctx => ctx.getImageData(0,0,width,height)))

            ctx.drawImage(result.canvas, 0, 0)

        }

        paint()

        canvas.addEventListener("click", paint, true)
    }
);
